/**
 * CustomerCreditsTab — saldos a favor del cliente. Nacen de dos formas: una NC
 * sobre una factura ya pagada, o un cobro en el que el cliente pagó de más.
 * Desde acá se devuelven o se aplican a otra factura.
 */
import { todayISO } from '../../../lib/utils'
import { useState, useMemo } from 'react'
import { CreditCard, Banknote, Loader2, FileText, Filter } from 'lucide-react'
import { Button } from '@/components/Button'
import { Modal } from '@/components/ds/Modal'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { ToastContainer, useToast } from '@/components/Toast'
import { Autocomplete, type AutocompleteItem } from '@/components/Autocomplete'
import { labelCls } from '@/lib/formStyles'
import { useTenantLocale, formatNumber, formatDate } from '@/lib/locale'
import {
  useCustomerCredits,
  useRefundCredit,
  useApplyCredit,
  type CustomerCredit,
  type CreditStatus,
  type RefundPayload,
} from '@/modules/receivables/hooks/useCustomerCredits'
import { useAccounts } from '@/modules/accounting/hooks/useAccounting'
import { useSaleDocuments } from '@/modules/sales/hooks/useSales'
import { DataTable } from '@/components/ds/DataTable'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'

export default function CustomerCreditsTab() {
  const locale = useTenantLocale()
  const [status, setStatus] = useState<CreditStatus | ''>('pending')
  const [refundTarget, setRefundTarget] = useState<CustomerCredit | null>(null)
  const [applyTarget, setApplyTarget] = useState<CustomerCredit | null>(null)

  const { data, isLoading } = useCustomerCredits(status ? { status } : undefined)

  const fmt = (v: string) => `$${formatNumber(parseFloat(v || '0'), locale, 0)}`

  return (
    <div className="space-y-4">
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-3 flex items-center gap-2 flex-wrap shadow-card">
        <Filter size={14} className="text-content-subtle" />
        {(['pending', 'refunded', 'applied', ''] as const).map(s => (
          <button
            key={s || 'all'}
            onClick={() => setStatus(s)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              status === s ? 'bg-primary text-on-primary' : 'bg-surface-muted text-content-muted hover:bg-surface-muted'
            }`}
          >
            {s === 'pending' ? 'Pendientes' : s === 'refunded' ? 'Reembolsados' : s === 'applied' ? 'Aplicados' : 'Todos'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <TableSkeleton rows={4} cols={6} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={CreditCard}
          title="Sin créditos"
          hint={status === 'pending' ? 'No tienes créditos pendientes por resolver.' : 'No hay créditos con ese filtro.'}
        />
      ) : (
        <DataTable
          columns={[
            { key: 'customer_name', header: 'Cliente', render: c => <span className="font-medium text-content">{c.customer_name}</span> },
            {
              key: 'origin', header: 'Origen',
              render: c => (c.origin === 'overpayment' ? 'Pago en exceso' : `NC #${c.source_folio || '—'}`),
            },
            { key: 'source_reference_folio', header: 'Factura original', render: c => `#${c.source_reference_folio || '—'}` },
            {
              key: 'amount', header: 'Monto', numeric: true,
              render: c => <span className="text-success-content">{fmt(c.amount)}</span>,
            },
            {
              key: 'status', header: 'Estado',
              render: c => (
                <>
                  <StatusPill status={c.status} />
                  {c.status === 'applied' && c.applied_to_folio && (
                    <span className="text-xs text-content-subtle ml-1">→ #{c.applied_to_folio}</span>
                  )}
                </>
              ),
            },
            {
              key: 'created_at', header: 'Creado',
              render: c => <span className="text-xs">{formatDate(c.created_at, locale)}</span>,
            },
            {
              key: 'acciones', header: 'Acciones',
              render: c => c.status === 'pending' ? (
                <div className="flex gap-1 justify-end">
                  <Button variant="ghost" leftIcon={<Banknote size={13} />} onClick={() => setRefundTarget(c)}>
                    Reembolsar
                  </Button>
                  <Button variant="ghost" leftIcon={<FileText size={13} />} onClick={() => setApplyTarget(c)}>
                    Aplicar a factura
                  </Button>
                </div>
              ) : (
                <span className="text-xs text-content-subtle">Resuelto</span>
              ),
            },
          ]}
          rows={data}
          rowKey={c => c.id}
          minWidth={900}
          caption="Créditos y anticipos de clientes"
        />
      )}

      {refundTarget && (
        <RefundModal credit={refundTarget} onClose={() => setRefundTarget(null)} />
      )}
      {applyTarget && (
        <ApplyModal credit={applyTarget} onClose={() => setApplyTarget(null)} />
      )}
    </div>
  )
}

function StatusPill({ status }: { status: CreditStatus }) {
  const styles: Record<CreditStatus, string> = {
    pending: 'bg-warning-surface-strong text-warning-content',
    refunded: 'bg-success-surface-strong text-success-content',
    applied: 'bg-primary-100 text-primary',
  }
  const labels: Record<CreditStatus, string> = {
    pending: 'Pendiente',
    refunded: 'Reembolsado',
    applied: 'Aplicado',
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  )
}

// ─── Modal Reembolso ─────────────────────────────────────────────────────────

function RefundModal({ credit, onClose }: { credit: CustomerCredit; onClose: () => void }) {
  const locale = useTenantLocale()
  const toast = useToast()
  const refund = useRefundCredit()
  const { data: accounts } = useAccounts()
  const today = todayISO()

  const [form, setForm] = useState<RefundPayload>({
    refund_date: today,
    refund_method: 'transfer',
    refund_reference: '',
    bank_account_id: '',
    notes: '',
  })

  // Cuentas bancarias: tipo asset. Simplificación — el admin elige la correcta.
  const bankItems: AutocompleteItem[] = useMemo(() => {
    const list = (accounts as any)?.accounts || []
    return list
      .filter((a: any) => a.account_type === 'asset' && !a.is_group)
      .map((a: any) => ({ id: a.id, label: `${a.code} — ${a.name}` }))
  }, [accounts])

  const valid = form.bank_account_id && form.refund_reference.trim()

  const onSubmit = () => {
    if (!valid) return
    refund.mutate({ creditId: credit.id, payload: form }, {
      onSuccess: () => {
        toast.success('Reembolso registrado')
        onClose()
      },
      onError: () => toast.error('Error al registrar reembolso'),
    })
  }

  return (
    <Modal open onClose={onClose} title="Registrar reembolso" size="sm">
      <div className="space-y-3">
        <div className="bg-warning-surface border border-warning-border rounded-lg p-3 text-sm text-warning-content-strong">
          Crédito de <strong>{credit.customer_name}</strong> por{' '}
          <strong>${formatNumber(parseFloat(credit.amount), locale, 0)}</strong>. Registra acá el
          reembolso ya realizado (transferencia, cheque, etc.). Se generará un comprobante
          contable automático.
        </div>

        <TextField
          dense
          label="Fecha del reembolso"
          value={form.refund_date}
          onChange={e => setForm(f => ({ ...f, refund_date: e.target.value }))}
          inputProps={{ type: 'date' }}
        />

        <SelectField
          dense
          label="Método"
          value={form.refund_method}
          onChange={e => setForm(f => ({ ...f, refund_method: e.target.value as RefundPayload['refund_method'] }))}
        >
          <option value="transfer">Transferencia</option>
          <option value="check">Cheque</option>
          <option value="cash">Efectivo</option>
          <option value="card">Tarjeta</option>
          <option value="other">Otro</option>
        </SelectField>

        <TextField
          dense
          label="Referencia (nº transferencia / cheque)"
          value={form.refund_reference}
          onChange={e => setForm(f => ({ ...f, refund_reference: e.target.value }))}
          placeholder="Ej. TRX-12345"
        />

        <div>
          <label htmlFor="customercreditstab-bank-account-id" className={labelCls}>Cuenta bancaria</label>
          <Autocomplete id="customercreditstab-bank-account-id"
            items={bankItems}
            value={form.bank_account_id}
            onChange={(v) => setForm(f => ({ ...f, bank_account_id: v }))}
            placeholder="Buscar cuenta..."
          />
        </div>

        <TextField
          dense
          label="Notas (opcional)"
          multiline
          rows={2}
          value={form.notes}
          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
        />
      </div>

      <div className="py-3 border-t border-divider flex justify-end gap-2 bg-surface sticky bottom-0">
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={onSubmit} disabled={!valid || refund.isPending}
                leftIcon={refund.isPending ? <Loader2 size={14} className="animate-spin" /> : undefined}>
          Registrar reembolso
        </Button>
      </div>

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </Modal>
  )
}

// ─── Modal Aplicar a factura ─────────────────────────────────────────────────

function ApplyModal({ credit, onClose }: { credit: CustomerCredit; onClose: () => void }) {
  const locale = useTenantLocale()
  const toast = useToast()
  const apply = useApplyCredit()
  const [targetId, setTargetId] = useState('')
  const [notes, setNotes] = useState('')

  // Facturas pendientes del mismo cliente
  const { data: docsData } = useSaleDocuments({
    customer_id: credit.customer,
    status: 'accepted',
    page_size: 200,
  })

  const items: AutocompleteItem[] = useMemo(() => {
    const docs = docsData?.documents || []
    return docs
      .filter((d: any) => (d.document_type === 'FE' || d.document_type === 'BE') && parseFloat(d.outstanding_amount || '0') > 0)
      .map((d: any) => ({
        id: d.id,
        label: `#${d.localization_data?.folio || d.id.slice(0, 8)} — ${formatNumber(parseFloat(d.total), locale, 0)}`,
        sublabel: `Saldo: $${formatNumber(parseFloat(d.outstanding_amount), locale, 0)}`,
      }))
  }, [docsData, locale])

  const onSubmit = () => {
    if (!targetId) return
    apply.mutate({ creditId: credit.id, payload: { target_document_id: targetId, notes } }, {
      onSuccess: () => {
        toast.success('Crédito aplicado')
        onClose()
      },
      onError: () => toast.error('Error al aplicar crédito'),
    })
  }

  return (
    <Modal open onClose={onClose} title="Aplicar crédito a factura" size="sm">
      <div className="space-y-3">
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 text-sm text-primary-hover">
          Aplicaremos el crédito de <strong>${formatNumber(parseFloat(credit.amount), locale, 0)}</strong>{' '}
          de <strong>{credit.customer_name}</strong> como pago de otra factura suya. Si el saldo
          pendiente de la factura es menor, se aplica solo el monto necesario.
        </div>

        <div>
          <label htmlFor="customercreditstab-factura-destino" className={labelCls}>Factura destino</label>
          <Autocomplete id="customercreditstab-factura-destino"
            items={items}
            value={targetId}
            onChange={setTargetId}
            placeholder="Buscar factura pendiente..."
          />
          {items.length === 0 && (
            <p className="text-xs text-content-subtle mt-1">
              No hay facturas pendientes para este cliente.
            </p>
          )}
        </div>

        <TextField
          dense
          label="Notas (opcional)"
          multiline
          rows={2}
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />
      </div>

      <div className="py-3 border-t border-divider flex justify-end gap-2 bg-surface sticky bottom-0">
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={onSubmit} disabled={!targetId || apply.isPending}
                leftIcon={apply.isPending ? <Loader2 size={14} className="animate-spin" /> : undefined}>
          Aplicar crédito
        </Button>
      </div>

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </Modal>
  )
}
